import { FC, useEffect, useState } from 'react';

import type { SanityLoadingBlock, SanityQuizTheme } from '@lib/sanity/types';

type LoadingBlockProps = Pick<
  SanityLoadingBlock,
  'rotatingMessages' | 'messageCycleIntervalMs' | 'errorText'
> & {
  theme?: SanityQuizTheme;
  isLoading: boolean;
  hasError: boolean;
};

const LOADING_DOTS_COUNT = 4;

const LoadingBlock: FC<LoadingBlockProps> = ({
  rotatingMessages,
  messageCycleIntervalMs = 2500,
  errorText,
  isLoading,
  hasError
}) => {
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);

  useEffect(() => {
    if (!isLoading || rotatingMessages.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentMessageIndex(
        (previous) => (previous + 1) % rotatingMessages.length
      );
    }, messageCycleIntervalMs);

    return () => clearInterval(interval);
  }, [isLoading, rotatingMessages.length, messageCycleIntervalMs]);

  if (hasError) {
    return (
      <section className="flex min-h-screen items-center justify-center px-6">
        <p className="font-body text-base text-primary-600">
          {errorText ?? 'Something went wrong. Please try again.'}
        </p>
      </section>
    );
  }

  return (
    <section className="flex min-h-screen items-center justify-center px-6">
      <div className="text-center">
        {/* Pulsing ring animation */}
        <div className="mx-auto mb-10 flex items-center justify-center gap-3">
          {Array.from({ length: LOADING_DOTS_COUNT }).map((_, index) => (
            <div
              key={index}
              className="animate-dot-pulse rounded-full h-2.5 w-2.5 bg-primary-600"
              style={{
                animationDelay: `${index * 200}ms`
              }}
            />
          ))}
        </div>

        <div className="space-y-2">
          {rotatingMessages.map((message, index) => (
            <p
              key={message}
              className="font-heading transition-all duration-700 italic text-base text-neutral-500"
              style={{
                opacity: index <= currentMessageIndex ? 1 : 0,
                transform:
                  index <= currentMessageIndex
                    ? 'translateY(0)'
                    : 'translateY(8px)'
              }}
            >
              {message}
            </p>
          ))}
        </div>
      </div>
    </section>
  );
};

export default LoadingBlock;
